import type { RoofHeight } from "@/data/vehicleFamilies";

interface Props {
  passengers: number;
  cargoLabel?: string;
  roof?: RoofHeight;
  layout?: "captain" | "bench";
  accent?: string;
  vehicleLabel?: string;
  hideCaption?: boolean;
}

/**
 * Top-down seating chart styled to match the reference illustration.
 */
export default function SeatingDiagram({
  passengers,
  vehicleLabel,
  hideCaption = false,
}: Props) {
  // ---- Layout: cab (2) + rear rows ----
  const rear = Math.max(0, passengers - 2);

  // Build rear rows from the BACK forward. Use rows of 3 by default,
  // last (front-most) row may be 2 if remainder is 2.
  const rows: number[] = [];
  let remaining = rear;
  while (remaining > 0) {
    if (remaining >= 3) {
      rows.push(3);
      remaining -= 3;
    } else {
      rows.push(remaining);
      remaining = 0;
    }
  }
  // rows[0] is the row directly behind the cab; last entry is the rear row.
  // Mark the rear-most row as removable.
  const removableRowIdx = rows.length - 1;

  // ---- SVG geometry ----
  const W = 520;
  const H = 240;

  // Van body (nose right). Cabin nose is a rounded curve.
  const bodyT = 30;
  const bodyB = 190;
  const bodyL = 30;
  const bodyR = W - 30;
  const noseW = 70; // hood length

  // Cabin (interior) box where seats live, excluding hood
  const cabL = bodyL + 14;
  const cabR = bodyR - noseW - 8;
  const cabT = bodyT + 12;
  const cabB = bodyB - 12;

  // Cab seats sit in the front portion (right side of cabin)
  const cabSeatX = cabR - 22;
  const driverY = cabT + 22;          // top = driver side
  const passengerY = cabB - 22;       // bottom = front passenger

  // Rear rows fill from just left of the cab seats back to the rear wall
  const rowsAreaR = cabSeatX - 34;
  const rowsAreaL = cabL + 6;
  const rowsAreaW = rowsAreaR - rowsAreaL;
  const rowGap = rows.length > 0 ? rowsAreaW / rows.length : 0;

  const seatW = 22;
  const seatH = 22;

  // Per-row x positions (center of each row, working back-to-front)
  const rowXs = rows.map((_, i) => rowsAreaR - rowGap / 2 - i * rowGap);

  // Y positions for a row of N seats stacked top-to-bottom in cabin
  const rowYs = (count: number) => {
    const top = cabT + 14;
    const bot = cabB - 14;
    const span = bot - top;
    if (count === 1) return [(top + bot) / 2];
    return Array.from({ length: count }).map(
      (_, i) => top + (span * i) / (count - 1),
    );
  };

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label={`${passengers} passenger van seating chart`}
      >
        {/* ---- Van body outline ---- */}
        {/* Main rectangle with rounded rear (left) and curved nose (right) */}
        <path
          d={`
            M ${bodyL + 14} ${bodyT}
            L ${bodyR - noseW} ${bodyT}
            Q ${bodyR - 6} ${bodyT + 4} ${bodyR - 2} ${bodyT + 30}
            L ${bodyR} ${(bodyT + bodyB) / 2 - 10}
            Q ${bodyR + 2} ${(bodyT + bodyB) / 2} ${bodyR} ${(bodyT + bodyB) / 2 + 10}
            L ${bodyR - 2} ${bodyB - 30}
            Q ${bodyR - 6} ${bodyB - 4} ${bodyR - noseW} ${bodyB}
            L ${bodyL + 14} ${bodyB}
            Q ${bodyL} ${bodyB} ${bodyL} ${bodyB - 14}
            L ${bodyL} ${bodyT + 14}
            Q ${bodyL} ${bodyT} ${bodyL + 14} ${bodyT}
            Z
          `}
          fill="#e5e5e7"
          stroke="#1a1a1a"
          strokeWidth={2}
        />

        {/* Hood divider line — separates engine bay from cabin */}
        <line
          x1={cabR + 6}
          y1={bodyT + 6}
          x2={cabR + 6}
          y2={bodyB - 6}
          stroke="#1a1a1a"
          strokeWidth={1.5}
          opacity={0.55}
        />

        {/* Sliding door cutout on the bottom side */}
        <line
          x1={cabL + rowsAreaW * 0.35}
          y1={bodyB}
          x2={cabL + rowsAreaW * 0.85}
          y2={bodyB}
          stroke="#c0392b"
          strokeWidth={3}
        />

        {/* Front wheels (visible nubs) */}
        <rect x={bodyR - 18} y={bodyT - 4} width={14} height={6} rx={2} fill="#1a1a1a" />
        <rect x={bodyR - 18} y={bodyB - 2} width={14} height={6} rx={2} fill="#1a1a1a" />
        {/* Rear wheels */}
        <rect x={bodyL - 4} y={bodyT - 4} width={14} height={6} rx={2} fill="#1a1a1a" />
        <rect x={bodyL - 4} y={bodyB - 2} width={14} height={6} rx={2} fill="#1a1a1a" />

        {/* ---- Cab seats ---- */}
        {/* Front passenger (left side, top) */}
        <Seat x={cabSeatX} y={driverY} w={seatW} h={seatH} />
        {/* Driver (with steering wheel) */}
        <Seat x={cabSeatX} y={passengerY} w={seatW} h={seatH} />
        <circle
          cx={cabSeatX + seatW + 8}
          cy={passengerY + seatH / 2}
          r={5}
          fill="none"
          stroke="#1a1a1a"
          strokeWidth={1.5}
        />

        {/* ---- Rear rows ---- */}
        {rows.map((count, i) => {
          const x = rowXs[i] - seatW / 2;
          const ys = rowYs(count);
          const isRemovable = i === removableRowIdx;
          return (
            <g key={i}>
              {ys.map((y, j) => (
                <Seat
                  key={j}
                  x={x}
                  y={y - seatH / 2}
                  w={seatW}
                  h={seatH}
                  removable={isRemovable}
                />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Caption + legend */}
      {!hideCaption && (
      <div className="mt-4 text-center">
        <p className="font-display font-extrabold text-lg tracking-wider text-[#1a1a1a]">
          SEATING CHART
        </p>
        <p className="font-display font-bold text-base text-[#1a1a1a] mt-0.5">
          {passengers} Passenger Van
        </p>
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-[#1a1a1a]">
          <span className="inline-block w-3.5 h-3.5 rounded-sm bg-[#d97a6f] border border-[#1a1a1a]" />
          <span>= Removable Seats</span>
        </div>
        {vehicleLabel && (
          <p className="mt-3 text-xs uppercase tracking-widest font-display font-bold text-[#1a1a1a]/60">
            {vehicleLabel}
          </p>
        )}
      </div>
      )}
    </div>
  );
}

function Seat({
  x,
  y,
  w,
  h,
  removable = false,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  removable?: boolean;
}) {
  const fill = removable ? "#d97a6f" : "#5a5a5a";
  return (
    <g>
      {/* seat back (thin bar at top) */}
      <rect x={x} y={y} width={w} height={4} rx={1.5} fill="#1a1a1a" />
      {/* seat cushion */}
      <rect
        x={x}
        y={y + 4}
        width={w}
        height={h - 4}
        rx={3}
        fill={fill}
        stroke="#1a1a1a"
        strokeWidth={1.2}
      />
    </g>
  );
}
